const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    --ctl-font: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);

    display: block;
  }

  .wrap{
    display: flex;
    align-items: center;
    gap: var(--gap, var(--ctl-space-3));
    padding-left: var(--inset, 0px);
    padding-right: var(--inset, 0px);
    min-height: var(--ctl-border-width-thin);
  }

  .line{
    flex: 1 1 auto;
    height: var(--ctl-border-width-thin);
    background: var(--ctl-color-border);
  }

  .label{
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: var(--ctl-font);
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-extrabold);
    letter-spacing: var(--ctl-tracking-wide);
    color: var(--ctl-color-muted);
    background: var(--ctl-color-surface);
    padding: 0 var(--ctl-divider-padding);
    border-radius: var(--ctl-radius-full);
    border: var(--ctl-border-width-thin) solid color-mix(in srgb, var(--ctl-color-border) 70%, transparent);
    white-space: nowrap;
  }

  /* Horizontal default */
  :host(:not([orientation]), [orientation="horizontal"]) .wrap{
    flex-direction: row;
  }

  /* Vertical */
  :host([orientation="vertical"]){
    display: inline-block;
  }
  :host([orientation="vertical"]) .wrap{
    flex-direction: column;
    padding-left: 0;
    padding-right: 0;
    padding-top: var(--inset, 0px);
    padding-bottom: var(--inset, 0px);
    gap: var(--gap, var(--ctl-space-3));
    min-width: var(--ctl-border-width-thin);
    height: var(--len, 100%);
  }
  :host([orientation="vertical"]) .line{
    width: var(--ctl-border-width-thin);
    height: auto;
  }
  :host([orientation="vertical"]) .label{
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    padding: var(--ctl-divider-padding) 0;
  }

  /* No label: collapse gap a bit */
  :host([no-label]) .wrap{
    gap: 0;
  }
</style>

<div class="wrap" part="wrap">
  <span class="line" part="line-start"></span>
  <span id="label" class="label" part="label" hidden></span>
  <span class="line" part="line-end"></span>
</div>
`;

export class CtlDivider extends HTMLElement {
  static get observedAttributes() {
    return ["orientation", "label", "inset", "gap", "length"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._labelEl = this.shadowRoot.getElementById("label");
  }

  connectedCallback() {
    // defaults
    if (!this.hasAttribute("orientation")) this.setAttribute("orientation", "horizontal");
    this._syncA11y();
    this._sync();
  }

  attributeChangedCallback() {
    this._syncA11y();
    this._sync();
  }

  // attrs
  get orientation() { return this.getAttribute("orientation") || "horizontal"; }
  set orientation(v) {
    const o = (v === "vertical") ? "vertical" : "horizontal";
    this.setAttribute("orientation", o);
  }

  get label() { return this.getAttribute("label") || ""; }
  set label(v) {
    v == null || v === "" ? this.removeAttribute("label") : this.setAttribute("label", String(v));
  }

  get inset() { return this.getAttribute("inset") || ""; }
  set inset(v) {
    v == null || v === "" ? this.removeAttribute("inset") : this.setAttribute("inset", String(v));
  }

  get gap() { return this.getAttribute("gap") || ""; }
  set gap(v) {
    v == null || v === "" ? this.removeAttribute("gap") : this.setAttribute("gap", String(v));
  }

  // for vertical: height control (e.g. "120px"). optional.
  get length() { return this.getAttribute("length") || ""; }
  set length(v) {
    v == null || v === "" ? this.removeAttribute("length") : this.setAttribute("length", String(v));
  }

  _syncA11y() {
    const o = this.orientation === "vertical" ? "vertical" : "horizontal";
    this.setAttribute("role", "separator");
    this.setAttribute("aria-orientation", o);

    // If label is present, it’s still a separator; label is visual.
    // If you want screen readers to announce it, you can set aria-label externally.
  }

  _sync() {
    // normalize orientation
    const o = this.orientation;
    if (!["horizontal", "vertical"].includes(o)) this.setAttribute("orientation", "horizontal");

    // label
    const lbl = this.getAttribute("label");
    const hasLabel = !!(lbl && String(lbl).trim().length);
    this.toggleAttribute("no-label", !hasLabel);

    if (hasLabel) {
      this._labelEl.hidden = false;
      this._labelEl.textContent = String(lbl);
    } else {
      this._labelEl.hidden = true;
      this._labelEl.textContent = "";
    }

    // spacing controls via CSS vars (accept any CSS unit)
    const inset = this.getAttribute("inset");
    if (inset) this.style.setProperty("--inset", inset);
    else this.style.removeProperty("--inset");

    const gap = this.getAttribute("gap");
    if (gap) this.style.setProperty("--gap", gap);
    else this.style.removeProperty("--gap");

    const len = this.getAttribute("length");
    if (len) this.style.setProperty("--len", len);
    else this.style.removeProperty("--len");
  }
}

customElements.define("ctl-divider", CtlDivider);
